import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import "./style.css";

const Home = () => {
  const [activeWhyTab, setActiveWhyTab] = useState(0);
  const [showNavQR, setShowNavQR] = useState(false);
  const [showHeroQR, setShowHeroQR] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const whyIntervalRef = useRef(null);
  const heroCtaRef = useRef(null);
  const [heroQRPos, setHeroQRPos] = useState({ top: 0, left: 0 });

  // 计算 QR 弹窗位置（fixed 定位）
  useEffect(() => {
    if (showHeroQR && heroCtaRef.current) {
      const rect = heroCtaRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        // 移动端居中显示
        setHeroQRPos({ top: rect.bottom + 12, left: rect.left + rect.width / 2 - 92 });
      } else {
        setHeroQRPos({ top: rect.bottom + 12, left: rect.left });
      }
    }
  }, [showHeroQR]);

  // Scroll reveal effect
  useEffect(() => {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: "0px 0px -50px 0px" });

    document.querySelectorAll(".reveal").forEach(el => revealObserver.observe(el));
    return () => revealObserver.disconnect();
  }, []);

  // Number counter animation
  useEffect(() => {
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const target = parseInt(el.dataset.target);
          const unitSwitch = el.hasAttribute('data-unit-switch');
          const duration = 2000;
          const startTime = performance.now();
          
          const animate = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(eased * target);
            if (unitSwitch) {
              // Phase 1: count 0→10000 with 万+ suffix; at end switch to 1亿+
              if (progress < 1) {
                el.innerHTML = current + '<span>万+</span>';
              } else {
                el.innerHTML = '1<span>亿+</span>';
              }
            } else {
              const suffixEl = el.querySelector("span");
              const suffix = suffixEl ? suffixEl.outerHTML : "";
              el.innerHTML = current + suffix;
            }
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
          counterObserver.unobserve(el);
        }
      });
    }, { threshold: 0.5 });

    document.querySelectorAll(".stat-num[data-target]").forEach(el => counterObserver.observe(el));
    return () => counterObserver.disconnect();
  }, []);

  // Why auto carousel
  useEffect(() => {
    whyIntervalRef.current = setInterval(() => {
      setActiveWhyTab(prev => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(whyIntervalRef.current);
  }, []);

  const handleWhyTabClick = (index) => {
    setActiveWhyTab(index);
    clearInterval(whyIntervalRef.current);
    whyIntervalRef.current = setInterval(() => {
      setActiveWhyTab(prev => (prev + 1) % 4);
    }, 4000);
  };

  // Features data
  const features = [
    { img: "/assets/gongneng1.jpg", title: "接龙打卡 & 信息收集", desc: "支持填空题、单多选、长文本、日期、定位、图片、语音、视频、手写签名、文件上传，可设置各种填写规则" },
    { img: "/assets/gongneng2.jpg", title: "定位打卡", desc: "签到打卡，支持收集实时定位、限定范围打卡、水印拍照打卡等，防代打卡神器" },
    { img: "/assets/gongneng3.jpg", title: "服务预约", desc: "预约时间、预约项目，灵活设置预约规则，方便管理与统计" },
    { img: "/assets/gongneng4.jpg", title: "周期打卡", desc: "支持按天/周/月/工作日等打卡规则，设置好后自动提醒，一次设置永久使用" },
    { img: "/assets/gongneng5.jpg", title: "信息核查", desc: "可以进行信息和图片的查询，快速核查比对，数据一目了然" },
    { img: "/assets/gongneng6.jpg", title: "商品接龙", desc: "可以设置商品进行加购，统计数量，也可以直接购买支付，支持社区团购" },
    { img: "/assets/gongneng7.jpg", title: "投票评选", desc: "可以进行简单投票、匿名投票、活动评选拉票等，多种模式自由切换" },
    { img: "/assets/gongneng8.jpg", title: "在线考试", desc: "导入试卷，设置考试规则，进行考试和自动打分，在线测评轻松完成" },
    { img: "/assets/gongneng9.jpg", title: "在线选课", desc: "可以按课程设置，家长或者参与者进行选课，名额自动管控" },
  ];

  // Pain points data
  const painPoints = [
    { icon: "😫", color: "red", title: "群里刷屏接龙，信息混乱", desc: "消息被淹没，错接、漏接频发，统计时头都大了", solution: "秒应解法：接龙表单自动汇总，群消息免刷屏" },
    { icon: "📊", color: "orange", title: "收集后还要手动整理 Excel", desc: "复制粘贴几小时，图片还要一张张改名保存", solution: "秒应解法：一键导出 Excel，图片自动按姓名命名打包" },
    { icon: "👥", color: "blue", title: "谁打卡了谁没打，完全不清楚", desc: "群发通知没人回，还要一个个私聊催交", solution: "秒应解法：导入名单自动标红未打卡，未打卡一键提醒" },
    { icon: "📅", color: "green", title: "周期性任务总要重复创建", desc: "每天打卡、每周填表，每次都要重新设置太麻烦", solution: "秒应解法：按天/周/月/工作日自动循环，设置一次永久使用" },
  ];

  // Scenes data
  const scenes = [
    { tag: "👨‍🏫 学校老师", title: "家校沟通 & 班级管理", desc: "作业提交、健康打卡、活动报名、家长签字，所有收集工作一个链接搞定，数据自动汇总到 Excel，再也不用一张一张保存图片。", features: ["学生作业在线提交与批改", "每日健康打卡自动统计", "家长签字确认一键导出"] },
    { tag: "🎓 辅导员 & 班委", title: "班级事务 & 活动组织", desc: "班会签到、活动报名、团建投票、信息统计，导入全班名单后谁完成谁没完成一目了然，还能一键提醒未打卡的同学。", features: ["班会签到实时查看", "班级投票匿名公平", "团建活动快速报名"], reverse: true },
    { tag: "💼 企业行政", title: "公司内勤 & 员工管理", desc: "考勤打卡、会议预约、物资领用、团建投票，定位打卡防作弊，周期性任务自动提醒，行政效率提升 10 倍。", features: ["员工定位打卡防代打", "会议室预约智能管理", "物资领用统计一目了然"] },
  ];

  // Why tabs data
  const whyTabs = [
    { num: "01", title: "一键导出，省事省时", desc: "收集的各类信息可一键导出汇总好的 Excel 表格，图片可导出为图片嵌入 Excel 表格，也可导出为按格式要求统一命名好的图片压缩包，文件也是自动汇总导出文件压缩包。" },
    { num: "02", title: "名单导入，追踪无忧", desc: "发布微信群内接龙时，可以导入名单，成员谁填写谁未填写一目了然。支持导入几千上万人名单，同时打卡，并且导出时还可分类导出汇总好。" },
    { num: "03", title: "种类丰富，不刷屏不整理", desc: "能收集的信息种类多，满足日常微信群打卡、信息收集的各类需求，不刷屏，免整理，省时省事省心。免费使用，教育场景更友好。" },
    { num: "04", title: "安全合规，数据有保障", desc: "教育部备案、阿里云数据支持，数据传输加密，隐私保护符合国家标准。支持数据导入导出，你拥有的数据永远属于你。" },
  ];

  // Why images data
  const whyImages = [
    '<img src="/assets/youshi1.jpg" alt="一键导出" style="width:100%; height:auto; border-radius:20px; display:block;" />',
    '<img src="/assets/youshi2.jpg" alt="名单导入" style="width:100%; height:auto; border-radius:20px; display:block;" />',
    '<img src="/assets/youshi3.jpg" alt="种类丰富" style="width:100%; height:auto; border-radius:20px; display:block;" />',
    '<img src="/assets/youshi4.jpg" alt="安全合规" style="width:100%; height:auto; border-radius:20px; display:block;" />',
  ];

  // FAQ data
  const faqs = [
    { q: "秒应是免费的吗？", a: '基础功能完全<strong>免费</strong>使用，包括接龙打卡、信息收集、数据导出等核心功能。高级功能如超大名单导入等也有免费额度。' },
    { q: "参与者需要注册账号吗？", a: "<strong>不需要</strong>。点击链接即可填写，微信小程序直接打卡，真正零门槛使用。" },
    { q: "怎么导入全班名单？", a: '支持 Excel 一键导入，可导入<strong>几千上万人名单</strong>。导入后谁填写谁未填写一目了然，未打卡的还能一键提醒。' },
    { q: "收集的图片能按姓名命名吗？", a: '可以！图片导出时<strong>自动按"姓名_班级_序号"格式命名</strong>，也可选择嵌入 Excel 或打包下载，省去手动整理的时间。' },
    { q: "定位打卡能防代打卡吗？", a: "支持<strong>实时定位</strong>、限定范围打卡、水印拍照打卡，多种方式确保打卡真实性，有效防止代打卡行为。" },
    { q: "周期打卡怎么设置自动提醒？", a: "创建时选择<strong>按天/周/月/工作日</strong>循环，设置好规则后系统会自动提醒参与者打卡，无需每次手动创建。" },
    { q: "数据安全吗？", a: '秒应<strong>教育部备案</strong>，采用<strong>阿里云数据存储</strong>，数据传输加密，隐私保护符合国家标准，可放心使用。' },
    { q: "和问卷星/腾讯文档有什么区别？", a: '秒应专为<strong>微信群场景</strong>优化：名单导入管理、图片自动命名导出、周期自动提醒、免注册参与——这些是问卷工具不具备的。' },
  ];

  return (
    <div className="home-page">
      {/* NAV */}
      <nav>
        <div className="container">
          <Link to="/" className="logo">
            <img src="/assets/logo.jpg" alt="秒应" className="logo-img" style={{width:32,height:32,borderRadius:8,objectFit:"cover"}} />
            秒应
          </Link>
          <div className={`nav-menu ${mobileMenuOpen ? 'open' : ''}`}>
            <ul className="nav-links">
              <li><a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); setMobileMenuOpen(false); }}>首页</a></li>
              <li><a href="#features" onClick={() => setMobileMenuOpen(false)}>核心功能</a></li>
              <li><a href="#scenes" onClick={() => setMobileMenuOpen(false)}>使用场景</a></li>
              <li><a href="#why" onClick={() => setMobileMenuOpen(false)}>差异化优势</a></li>
              <li><a href="#faq" onClick={() => setMobileMenuOpen(false)}>常见问题</a></li>
              <li><Link to="/help" onClick={() => setMobileMenuOpen(false)}>操作教程</Link></li>
              <li><Link to="/helpcenter" onClick={() => { window.scrollTo(0, 0); setMobileMenuOpen(false); }}>帮助中心</Link></li>
            </ul>
            <div className="nav-menu-actions">
              <button className="nav-menu-btn" onClick={() => { setShowNavQR(!showNavQR); }}>📱 手机版</button>
              {showNavQR && (
                <div className="nav-menu-qr">
                  <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" />
                  <span>微信扫码使用</span>
                </div>
              )}
              <a href="https://miaoying.hui51.cn/" target="_blank" rel="noopener noreferrer" className="nav-menu-btn">💻 网页版</a>
            </div>
          </div>
          <div className="nav-right">
            <div className="nav-btns">
              <div className="download-btn-wrapper">
                <button className="nav-btn" onClick={() => setShowNavQR(!showNavQR)}>📱 手机版</button>
                {showNavQR && (
                  <div className="qr-popup">
                    <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" />
                  </div>
                )}
              </div>
              <a href="https://miaoying.hui51.cn/" target="_blank" rel="noopener noreferrer" className="nav-btn">💻 网页版</a>
            </div>
            <button className="nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="float-shape s1"></div>
        <div className="float-shape s2"></div>
        <div className="float-shape s3"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-brand-name hero-entrance-brand">秒应</div>
              <div className="hero-sub-title hero-entrance-sub">群接龙打卡报名预约工具</div>
              <div className="hero-desc hero-entrance-desc">
                老师、班委、行政都在用的效率神器<br />
                导入名单一键打卡，数据自动汇总 Excel<br />
                图片按姓名命名导出，省时省力不刷屏
              </div>
              <div className="hero-features hero-entrance-features">
                <span className="hero-feature-item"><span className="item-icon green">✓</span>免费使用</span>
                <span className="hero-feature-item"><span className="item-icon blue">⚡</span>免注册</span>
                <span className="hero-feature-item"><span className="item-icon purple">🔒</span>数据安全</span>
              </div>
              <div className="hero-btns hero-entrance-btns">
                <div className="hero-cta-wrapper" ref={heroCtaRef}>
                  <span 
                    className="btn-primary"
                    onMouseEnter={() => { if (window.innerWidth > 768) setShowHeroQR(true); }}
                    onMouseLeave={() => { if (window.innerWidth > 768) setShowHeroQR(false); }}
                    onClick={(e) => { if (window.innerWidth <= 768) setShowHeroQR(v => !v); }}
                  >
                    立即免费使用 →
                  </span>
                </div>
                <a href="#features" className="btn-secondary">了解更多</a>
              </div>
            </div>
            <div className="hero-visual">
              <div className="phone-mockup hero-entrance-phone1">
                <img src="/assets/phone1.jpg" alt="手机截图1" style={{width: "100%", height: "auto", borderRadius: "28px", display: "block"}} />
              </div>
              <div className="phone-mockup hero-entrance-phone2">
                <img src="/assets/phone2.jpg" alt="手机截图2" style={{width: "100%", height: "auto", borderRadius: "28px", display: "block"}} />
              </div>
              <div className="hero-float-label f4 hero-entrance-float4"><span className="float-dot purple"></span>多种表单</div>
              <div className="hero-float-label f2 hero-entrance-float2"><span className="float-dot blue"></span>批量导出</div>
              <div className="hero-float-label f1 hero-entrance-float1"><span className="float-dot green"></span>名单导入</div>
              <div className="hero-float-label f3 hero-entrance-float3"><span className="float-dot orange"></span>自动提醒</div>
              <div className="hero-float-label f5 hero-entrance-float5"><span className="float-dot pink"></span>快速统计</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item reveal"><div className="stat-num" data-target="10000" data-unit-switch>0<span>万+</span></div><div className="stat-label">用户信赖</div></div>
            <div className="stat-item reveal reveal-delay-1"><div className="stat-num" data-target="5">0<span>亿+</span></div><div className="stat-label">累计使用</div></div>
            <div className="stat-item reveal reveal-delay-2"><div className="stat-num" data-target="8">0<span>年</span></div><div className="stat-label">稳定服务</div></div>
            <div className="stat-item reveal reveal-delay-3"><div className="stat-num" data-target="9">0<span>大功能</span></div><div className="stat-label">全面覆盖</div></div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-number reveal">01</span>
            <span className="section-tag reveal reveal-delay-1">核心功能</span>
            <h2 className="section-title reveal reveal-delay-1">9 大功能，覆盖各种场景</h2>
            <p className="section-desc reveal reveal-delay-2">接龙打卡、信息收集、预约报名、在线考试，一个工具全搞定</p>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div key={i} className={`feature-card reveal reveal-delay-${i}`}>
                <div className="feature-img">
                  {f.img ? (
                    <img src={f.img} alt={f.title} style={{width: "100%", height: "100%", objectFit: "cover", display: "block"}} />
                  ) : (
                    "【图片】368×200"
                  )}
                </div>
                <div className="feature-content">
                  <div className="feature-header">
                    {f.icon && <div className={`feature-icon ${f.color}`}>{f.icon}</div>}
                    <h3>{f.title}</h3>
                  </div>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="pain">
        <div className="float-shape s1"></div>
        <div className="float-shape s2"></div>
        <div className="container">
          <div className="section-header">
            <span className="section-number reveal">02</span>
            <span className="section-tag reveal reveal-delay-1">痛点解决</span>
            <h2 className="section-title reveal reveal-delay-1">这些困扰，秒应都能解决</h2>
            <p className="section-desc reveal reveal-delay-2">微信群接龙刷屏、数据难整理、通知不到位——这些烦恼从此告别</p>
          </div>
          <div className="pain-grid">
            {painPoints.map((p, i) => (
              <div key={i} className={`pain-card reveal reveal-delay-${i}`}>
                <div className={`pain-icon ${p.color}`}>{p.icon}</div>
                <div className="pain-content">
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                  <div className="pain-solution">{p.solution}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SCENES */}
      <section className="scenes" id="scenes">
        <div className="float-shape s1"></div>
        <div className="container">
          <div className="section-header">
            <span className="section-number reveal">03</span>
            <span className="section-tag reveal reveal-delay-1">使用场景</span>
            <h2 className="section-title reveal reveal-delay-1">专为这些人设计</h2>
            <p className="section-desc reveal reveal-delay-2">老师、班委、行政，谁用谁省心</p>
          </div>
          {scenes.map((s, i) => (
            <div key={i} className={`scene-block ${s.reverse ? "reverse" : ""} reveal`}>
              <div className="scene-visual">
                <div className="scene-tag">{s.tag}</div>
                <div className="scene-img"><img src={`/assets/changjing${i + 1}.jpg`} alt={s.title} /></div>
              </div>
              <div className="scene-info">
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <div className="scene-features">
                  {s.features.map((f, j) => (
                    <span key={j} className="scene-feature">{f}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="why" id="why">
        <div className="container">
          <div className="section-header">
            <span className="section-number reveal">04</span>
            <span className="section-tag reveal reveal-delay-1">差异化优势</span>
            <h2 className="section-title reveal reveal-delay-1">为什么选择秒应</h2>
            <p className="section-desc reveal reveal-delay-2">对比其他工具，这些才是核心竞争力</p>
          </div>
          <div className="why-grid reveal">
            <div className="why-tabs">
              {whyTabs.map((t, i) => (
                <div key={i} className={`why-tab ${activeWhyTab === i ? "active" : ""}`} onClick={() => handleWhyTabClick(i)}>
                  <div className="why-tab-num">{t.num}</div>
                  <div>
                    <h4>{t.title}</h4>
                    <p>{t.desc}</p>
                  </div>
                </div>
              ))}
              <div className="why-progress">
                {[0,1,2,3].map(i => (
                  <div key={i} className={`why-progress-bar ${activeWhyTab === i ? "active" : ""}`}>
                    <div className="fill"></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="why-images">
              {whyImages.map((img, i) => (
                <div key={i} className={`why-image ${activeWhyTab === i ? "active" : ""}`} dangerouslySetInnerHTML={{__html: img}}></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="section-header">
            <span className="section-number reveal">05</span>
            <span className="section-tag reveal reveal-delay-1">常见问题</span>
            <h2 className="section-title reveal reveal-delay-1">有疑问？看这里</h2>
            <p className="section-desc reveal reveal-delay-2">热门问题一站式解答，帮你快速上手</p>
          </div>
          <div className="faq-grid">
            {faqs.map((f, i) => (
              <div key={i} className={`faq-card reveal reveal-delay-${i}`}>
                <div className="faq-question">{f.q}</div>
                <div className="faq-answer" dangerouslySetInnerHTML={{__html: f.a}}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <div className="cta-content reveal">
            <h2>现在就试试秒应</h2>
            <p>扫码立即体验，免费使用，无需注册</p>
            <div className="cta-qr-codes">
              <div className="qr-item">
                <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" className="qr-img" />
                <span className="qr-label">微信小程序</span>
              </div>
              <div className="qr-item">
                <img src="/assets/code4.6658546.jpg" alt="网页版二维码" className="qr-img" />
                <span className="qr-label">微信公众号</span>
              </div>
              <div className="qr-item">
                <img src="/assets/code3.563ecfb.jpg" alt="客服微信" className="qr-img" />
                <span className="qr-label">客服微信：jizhitech</span>
              </div>
            </div>
          </div>
        </div>
      </section>

 
      {/* Hero QR 弹窗通过 Portal 渲染到 body，避免祖先 animation/transform 影响 fixed 定位 */}
      {showHeroQR && createPortal(
        <>
          {/* 移动端遮罩层，点击关闭弹窗 */}
          {window.innerWidth <= 768 && (
            <div 
              style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998, background: "rgba(0,0,0,0.3)" }}
              onClick={() => setShowHeroQR(false)}
            />
          )}
          <div 
            className="hero-qr-popup" 
            style={{ position: "fixed", top: heroQRPos.top, left: heroQRPos.left, zIndex: 9999 }}
            onMouseEnter={() => { if (window.innerWidth > 768) setShowHeroQR(true); }}
            onMouseLeave={() => { if (window.innerWidth > 768) setShowHeroQR(false); }}
          >
            <img src="/assets/code1.8137c5d.jpg" alt="微信小程序码" />
            <span>微信扫码体验</span>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default Home;
